import api from './api';

// ============================================
// MODULAR CONFIG INTERFACES
// ============================================

export interface AMSConfig {
    handover_latency_reduction?: number;
    smart_sequencing_mode?: 'EOBT' | 'CTOT' | 'FCFS' | 'ASAT' | null;
}

export interface FASTGateConfig {
    pushback_reduction?: number;
    eligible_types?: string[];
    tractor_required?: boolean;
}

export interface TurnaroundMonitoringConfig {
    provider?: 'assaia' | 'zestiot' | 'other';
    turnaround_reduction?: number;
}

export interface InfraChangesConfig {
    infrastructure_types?: string[];
    new_taxiways?: any[];
    new_stands?: any[];
    tcf_reduction?: number;
    what_improves?: string;
    how_improves?: string;
    magnitude?: string;
}

export interface SOPConfig {
    simultaneous_pushback?: boolean;
    contiguous_pushback?: boolean;
    simultaneous_contiguous_pushback?: boolean;
    dynamic_routing?: boolean;
    adaptive_routing?: boolean;
    safety_blocking?: boolean;
}

export interface StandardRoutesConfig {
    routes_description?: string;
}

export interface HandoverPoint {
    id: string;
    name: string;
    lat: number;
    lon: number;
    contact_probability: number;
    contact_delay_s: number;
}

export interface CONOPSConfig {
    ams_zone_ids?: string[];
    handover_points?: HandoverPoint[];
    ci_node_safety_threshold?: number;
    sequencing_priority?: "AUTO" | "EOBT" | "CTOT";
    // legacy fields preserved for backward-compat
    ams_platforms?: string[];
    tower_platforms?: string[];
}

// ============================================
// SIMULATION INTERFACES
// ============================================

export interface Simulation {
    id: string;
    project_id: string;
    name: string;
    description?: string;
    status: 'DRAFT' | 'RUNNING' | 'COMPLETED' | 'FAILED';

    // Modular To-Be toggles
    is_baseline: boolean;
    ams_enabled: boolean;
    ams_config?: AMSConfig;
    fastgate_enabled: boolean;
    fastgate_config?: FASTGateConfig;
    turnaround_monitoring_enabled: boolean;
    turnaround_monitoring_config?: TurnaroundMonitoringConfig;
    infra_changes_enabled: boolean;
    infra_changes_config?: InfraChangesConfig;
    new_sop_enabled: boolean;
    new_sop_config?: SOPConfig;
    standard_routes_enabled?: boolean;
    standard_routes_config?: StandardRoutesConfig;
    dacams_improvements_enabled: boolean;

    // CONOPS
    conops_config?: CONOPSConfig;
    simulation_parameters?: Record<string, any>;

    // Results
    results_kpis?: Record<string, any>;
    results_comparative?: Record<string, any>;

    created_at: string;
    updated_at?: string;

    // Runtime-enriched fields (joined from Project data)
    project_name?: string;
    project_iata?: string;
    scenario_type?: string;
}

export interface SimulationCreate {
    project_id: string;
    name: string;
    description?: string;

    is_baseline?: boolean;
    ams_enabled?: boolean;
    ams_config?: AMSConfig;
    fastgate_enabled?: boolean;
    fastgate_config?: FASTGateConfig;
    turnaround_monitoring_enabled?: boolean;
    turnaround_monitoring_config?: TurnaroundMonitoringConfig;
    infra_changes_enabled?: boolean;
    infra_changes_config?: InfraChangesConfig;
    new_sop_enabled?: boolean;
    new_sop_config?: SOPConfig;
    standard_routes_enabled?: boolean;
    standard_routes_config?: StandardRoutesConfig;
    dacams_improvements_enabled?: boolean;

    conops_config?: CONOPSConfig;
    // GeoJSON CONOPS from the visual map editor (optional — supplements conops_config)
    conops_geojson?: Record<string, any>;
    simulation_parameters?: Record<string, any>;
}

// ============================================
// SIMULATIONS SERVICE
// ============================================

export const simulationsService = {
    /**
     * Get all simulations, optionally filtered by project
     */
    async getAll(projectId?: string): Promise<Simulation[]> {
        try {
            const params = projectId ? { project_id: projectId } : {};
            const response = await api.get('/simulations/', { params });
            return response.data;
        } catch (error) {
            console.warn('Error fetching simulations, returning empty array');
            return [];
        }
    },

    /**
     * Get a specific simulation by ID
     */
    async getOne(id: string): Promise<Simulation | null> {
        try {
            const response = await api.get(`/simulations/${id}`);
            return response.data;
        } catch (error) {
            console.warn('Error fetching simulation');
            return null;
        }
    },

    /**
     * Create a new simulation scenario
     */
    async create(data: SimulationCreate): Promise<Simulation> {
        const response = await api.post('/simulations/', data);
        return response.data;
    },

    /**
     * Update an existing simulation
     */
    async update(id: string, data: Partial<SimulationCreate>): Promise<Simulation> {
        const response = await api.put(`/simulations/${id}`, data);
        return response.data;
    },

    /**
     * Delete a simulation
     */
    async delete(id: string): Promise<void> {
        await api.delete(`/simulations/${id}`);
    },

    /**
     * Run/Execute a simulation (triggers Celery task)
     */
    async run(id: string): Promise<Simulation> {
        const response = await api.post(`/simulations/${id}/run`);
        return response.data;
    },

    /**
     * Get simulations for a specific DACAMS project
     */
    async getByProject(projectId: string): Promise<Simulation[]> {
        return this.getAll(projectId);
    }
};
